import { Host, h } from "@stencil/core";
import { tokenMap } from "../../../../common/utils";
import { ACTION_LIST_GROUP_EXPORT_PARTS, ACTION_LIST_GROUP_PARTS_DICTIONARY, ACTION_LIST_PARTS_DICTIONARY } from "../../../../common/reserved-names";
const EXPANDABLE_ID = "expandable";
export class ChActionListGroup {
    constructor() {
        this.caption = undefined;
        this.disabled = false;
        this.downloading = false;
        this.expandable = undefined;
        this.expanded = undefined;
        this.lazyLoad = false;
        this.metadata = undefined;
        this.parts = undefined;
        this.selected = false;
        this.showDownloadingSpinner = true;
    }
    #buttonRef;
    el;
    // /**
    //  * Fired when the item is being dragged.
    //  */
    // @Event() itemDragStart: EventEmitter<TreeViewItemDragStartInfo>;
    /**
     * Fired when the lazy control is expanded an its content must be loaded.
     */
    loadLazyContent;
    /**
     * Set the focus in the control if `expandable === true`.
     */
    async setFocus() {
        if (this.expandable && this.#buttonRef) {
            this.#buttonRef.focus();
        }
    }
    #getExpandedValue = () => this.expandable ? this.expanded ?? false : true;
    connectedCallback() {
        this.el.setAttribute("role", "listitem");
        this.el.setAttribute("part", ACTION_LIST_PARTS_DICTIONARY.GROUP);
        this.el.setAttribute("exportparts", ACTION_LIST_GROUP_EXPORT_PARTS);
    }
    render() {
        const hasContent = !this.lazyLoad;
        const expanded = hasContent && this.#getExpandedValue();
        return (h(Host, { key: '0944994f4bbee31eeb223fa845b1c13920f96ced' }, this.expandable ? (h("button", { "aria-controls": hasContent ? EXPANDABLE_ID : null, "aria-expanded": hasContent ? expanded.toString() : null, class: { action: true, "action--collapsed": !expanded }, disabled: this.disabled, part: tokenMap({
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.ACTION]: true,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.SELECTED]: this.selected,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.DISABLED]: this.disabled
            }), type: "button", ref: el => (this.#buttonRef = el) }, this.caption)) : (h("span", {
            // TODO: Test this case (accessibility)
            // aria-controls={hasContent ? EXPANDABLE_ID : null}
            // aria-expanded={hasContent ? expanded.toString() : null}
            class: "action", part: tokenMap({
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.CAPTION]: true,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.DISABLED]: this.disabled
            })
        }, this.caption)), hasContent && (h("ul", { key: 'c16e98f62ea84cc72a366428240ddcd926e4f057', "aria-busy": (!!this.downloading).toString(), "aria-live": this.downloading ? "polite" : null, class: {
                expandable: true,
                "expandable--collapsed": !expanded,
                "expandable--lazy-loaded": !this.downloading
            }, part: tokenMap({
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.EXPANDABLE]: true,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.EXPANDED]: expanded,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.COLLAPSED]: !expanded,
                [ACTION_LIST_GROUP_PARTS_DICTIONARY.LAZY_LOADED]: !this.downloading
            }), id: EXPANDABLE_ID }, h("slot", { key: '61c9e24f9758d141fadcdb47717b8fad21ed8da7' })))));
    }
    static get is() { return "ch-action-list-group"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["action-list-group.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-list-group.css"]
        };
    }
    static get properties() {
        return {
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attributes specifies the caption of the control"
                },
                "attribute": "caption",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "downloading": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify when items are being lazy loaded in the\ncontrol."
                },
                "attribute": "downloading",
                "reflect": false,
                "defaultValue": "false"
            },
            "expandable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If the item has a sub-tree, this attribute determines if the subtree is\ndisplayed."
                },
                "attribute": "expandable",
                "reflect": false
            },
            "expanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If the item has a sub-tree, this attribute determines if the subtree is\ndisplayed."
                },
                "attribute": "expanded",
                "reflect": false
            },
            "lazyLoad": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the items are lazy loaded when opening the first time the\ncontrol."
                },
                "attribute": "lazy-load",
                "reflect": false,
                "defaultValue": "false"
            },
            "metadata": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute represents additional info for the control that is included\nwhen dragging the item."
                },
                "attribute": "metadata",
                "reflect": false
            },
            "parts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in every DOM element of the control."
                },
                "attribute": "parts",
                "reflect": false
            },
            "selected": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the item is selected"
                },
                "attribute": "selected",
                "reflect": false,
                "defaultValue": "false"
            },
            "showDownloadingSpinner": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the downloading spinner when lazy loading the sub items of\nthe control."
                },
                "attribute": "show-downloading-spinner",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get events() {
        return [{
                "method": "loadLazyContent",
                "name": "loadLazyContent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the lazy control is expanded an its content must be loaded."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "setFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set the focus in the control if `expandable === true`.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
}
